import { Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Outlet, useHref, useLocation } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import { Backdrop } from '@sorare/core/src/atoms/loader/Backdrop';
import { TwitchPlayer } from '@sorare/core/src/components/TwitchPlayer';
import { Dialog } from '@sorare/core/src/components/dialog';
import {
  DialogKey,
  WithDialogs,
} from '@sorare/core/src/components/navigation/WithDialogs';
import { SidetabHandler as TypeformSidetabHandler } from '@sorare/core/src/components/typeform/SidetabHandler';
import { DepositAcceptedDialog } from '@sorare/core/src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/DepositAcceptedDialog';
import { IS_PRERENDER } from '@sorare/core/src/config';
import { StyleSheetManager } from '@sorare/core/src/contexts/StyleSheetManager';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { HrefLang } from '@sorare/core/src/contexts/intl/Provider';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import {
  useFeatureFlags,
  useLDClient,
} from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { lazy } from '@sorare/core/src/lib/retry';
import { Maintenance } from '@sorare/core/src/routing/Maintenance';

import { SpecificNewsDialogContent } from '@sorare/gameplay/src/components/activity/SpecificNews';

import { AppProviders } from 'AppProviders';
import { LazyBlockchainProviders } from 'components/BlockChainProviders/lazy';
import { MobileAppInit } from 'components/MobileAppInit';

const AnyCardPage = lazy(
  async () => import('components/AnyCardPage'),
  'AnyCardPageDialog'
);
const CreditClaimedDialog = lazy(
  async () =>
    import('@sorare/core/src/components/conversionCredit/CreditClaimedDialog'),
  'CreditClaimedDialog'
);
const AuctionDialog = lazy(
  async () =>
    import(
      '@sorare/marketplace/src/components/buyActions/BidField/BidPaymentFlow'
    ),
  'BidPaymentFlow'
);

const PrimaryOfferDialog = lazy(
  async () =>
    import(
      '@sorare/marketplace/src/components/primaryBuy/PrimaryBuyBuyField/PrimaryOfferBuyFieldWrapper/StandalonePrimaryBuyFlow'
    ),
  'StandalonePrimaryBuyFlow'
);

const VerifyPhoneNumber = lazy(
  async () => import('@sorare/core/src/components/user/VerifyPhoneNumber'),
  'VerifyPhoneNumber'
);

const LoanWelcomeScreen = lazy(
  async () => import('@sorare/marketplace/src/components/loan/WelcomeScreen'),
  'WelcomeScreen'
);

const Checkout = lazy(
  async () => import('@sorare/marketplace/src/components/cart/Checkout'),
  'Checkout'
);

const UnblockWallet = lazy(
  async () => import('@sorare/core/src/components/fiatWallet/UnblockWallet'),
  'UnblockWallet'
);
const TrckGroupDrawer = lazy(
  async () =>
    import('@sorare/gameplay/src/components/trckGroup/TrckGroupDrawer'),
  'TrckGroupDrawer'
);
const JoinConnectedTaskDialog = lazy(
  async () =>
    import('@sorare/core/src/components/missions/JoinConnectedTaskDialog'),
  'JoinConnectedTaskDialog'
);

const RoutedNewSaleDialog = lazy(
  async () =>
    import('@sorare/marketplace/src/components/offer/RoutedSaleDialog'),
  'RoutedNewSaleDialog'
);

function getOrCreateMeta(name: string): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name=${name}]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  return meta;
}

const PrerenderMeta = () => {
  // eslint-disable-next-line no-restricted-properties
  const initial = useRef(window.location.pathname);
  const location = useLocation();

  const href = useHref(location.pathname);

  useEffect(() => {
    if (href !== initial.current) {
      // https://docs.prerender.io/docs/11-best-practices
      const statusMeta = getOrCreateMeta('prerender-status-code');
      statusMeta.content = '301';
      const locationMeta = getOrCreateMeta('prerender-header');
      locationMeta.content = `Location: ${href}`;
    }
  }, [href]);
  return null;
};

export const InnerAppLayout = () => {
  const { currentUser } = useCurrentUserContext();
  const closeDialog = useCloseDialog();

  return (
    <WithDialogs
      dialogs={[
        {
          name: DialogKey.news,
          element: (
            <Suspense fallback={<Backdrop />}>
              <Dialog
                open
                maxWidth="xs"
                fullWidth
                onClose={() => closeDialog(DialogKey.news)}
              >
                <SpecificNewsDialogContent />
              </Dialog>
            </Suspense>
          ),
        },
        {
          name: DialogKey.paypalTransactionId,
          element: (
            <Suspense fallback={<Backdrop />}>
              <DepositAcceptedDialog />
            </Suspense>
          ),
        },
        {
          name: DialogKey.claimedConversionCredit,
          element: (
            <Suspense fallback={<Backdrop />}>
              <CreditClaimedDialog />
            </Suspense>
          ),
        },
        {
          name: DialogKey.card,
          element: (
            <Dialog
              open
              fullScreen
              hideHeader
              onClose={() => closeDialog(DialogKey.card)}
            >
              <Suspense fallback={<Backdrop />}>
                <AnyCardPage />
              </Suspense>
            </Dialog>
          ),
        },
        {
          name: DialogKey.auction,
          element: (
            <Suspense fallback={<Backdrop />}>
              <AuctionDialog />
            </Suspense>
          ),
        },
        {
          name: DialogKey.primaryOffer,
          element: (
            <Suspense fallback={<Backdrop />}>
              <PrimaryOfferDialog />
            </Suspense>
          ),
        },
        {
          name: DialogKey.unblockWallet,
          element: (
            <Suspense fallback={<Backdrop />}>
              <UnblockWallet />
            </Suspense>
          ),
        },
        {
          name: DialogKey.verifyPhone,
          element: (
            <Suspense fallback={<Backdrop />}>
              <VerifyPhoneNumber
                onCancel={() => closeDialog(DialogKey.verifyPhone)}
              />
            </Suspense>
          ),
        },
        {
          name: DialogKey.loanWelcomeScreen,
          element: (
            <Suspense fallback={<Backdrop />}>
              <LoanWelcomeScreen />
            </Suspense>
          ),
        },
        {
          name: DialogKey.checkout,
          element: (
            <Suspense fallback={<Backdrop />}>
              <Checkout />
            </Suspense>
          ),
        },
        {
          name: DialogKey.singleSaleOffer,
          element: (
            <Suspense fallback={<Backdrop />}>
              <RoutedNewSaleDialog />
            </Suspense>
          ),
        },
        {
          name: DialogKey.competitionDrawer,
          element: (
            <Suspense fallback={<Backdrop />}>
              <TrckGroupDrawer />
            </Suspense>
          ),
        },
        {
          name: DialogKey.connect_campaign,
          element: (
            <Suspense fallback={<Backdrop />}>
              <JoinConnectedTaskDialog />
            </Suspense>
          ),
        },
      ]}
    >
      {currentUser ? (
        <LazyBlockchainProviders>
          <TypeformSidetabHandler />
          <Outlet />
        </LazyBlockchainProviders>
      ) : (
        <Outlet />
      )}
    </WithDialogs>
  );
};

const GlobalBackground = createGlobalStyle`
  body {
    color: var(--c-white);
    background: var(--c-black);
  }
  `;

const LightMode = () => {
  const { lifecycle } = useLifecycle();
  const lightMode = lifecycle?.[LIFECYCLE.experimentalLightMode];
  useLayoutEffect(() => {
    if (lightMode) {
      document.documentElement.classList.add('light-mode');
    }
    return () => document.documentElement.classList.remove('light-mode');
  }, [lightMode]);

  return null;
};

export const OuterAppLayout = () => {
  const client = useLDClient();
  const {
    flags: { useMaintenance },
  } = useFeatureFlags();

  if (!client) {
    return null;
  }

  if (useMaintenance?.maintenance) {
    return (
      <Maintenance
        startDate={new Date(useMaintenance.start_date)}
        endDate={new Date(useMaintenance.end_date)}
      />
    );
  }

  return (
    <HelmetProvider>
      <GlobalBackground />
      <MobileAppInit />
      <HrefLang />
      {!!IS_PRERENDER && <PrerenderMeta />}
      <StyleSheetManager>
        <AppProviders>
          <LightMode />
          <Outlet />
          {!IS_PRERENDER && <TwitchPlayer />}
        </AppProviders>
      </StyleSheetManager>
    </HelmetProvider>
  );
};
